import React, { useState, useEffect } from 'react';
import { Button, SparkIcon, Input, Divider, Badge, AlertBanner, Skeleton, SparkDrawerPortal, DrawerItem, DropdownMenu, DataTable } from '@spark';

import StatisticsSection from '@/features/datasets/components/DataSelection/RawRecordsStatus/StatisticsSection';
import RawExampleModal from '@/features/datasets/components/DataSelection/RawRecordsStatus/RawExampleModal';
import { createColumns } from '@/features/datasets/components/DataSelection/RawRecordsStatus/tableColumns';
import { fetchRawRecordsStats, fetchRawRecords, fetchRawExample, fetchResponseOptions } from '@/features/datasets/components/DataSelection/RawRecordsStatus/service';

/**
 * Component for displaying the status of raw records processing
 */
const RawRecordsStatusTable = ({ currentUsecase, isLoading, statsData, setStatsData }) => {
  // State management
  const [recordsData, setRecordsData] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rawExampleVisible, setRawExampleVisible] = useState(false);
  const [rawExampleLoading, setRawExampleLoading] = useState(false);
  const [rawExampleData, setRawExampleData] = useState(null);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [responseOptions, setResponseOptions] = useState([]);
  const [loadingResponseOptions, setLoadingResponseOptions] = useState(false);
  const [searchResponseValue, setSearchResponseValue] = useState('');

  // Form state (replaces Form.useForm())
  const [formValues, setFormValues] = useState({
    searchQuery: '',
    rawPromptQuery: '',
    rawResponseQuery: '',
  });

  const [filters, setFilters] = useState({
    exampleStatus: undefined,
    responseStatus: undefined,
    searchQuery: '',
    rawPromptQuery: '',
    rawResponseQuery: '',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [filterSubmitted, setFilterSubmitted] = useState(false);

  // Calculate active filters count
  useEffect(() => {
    let count = 0;
    if (filters.exampleStatus) count++;
    if (filters.responseStatus) count++;
    if (filters.searchQuery) count++;
    if (filters.rawPromptQuery) count++;
    if (filters.rawResponseQuery) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  // Initial data loading
  useEffect(() => {
    if (currentUsecase && !isLoading) {
      fetchStats();
      fetchRecordsData(1, 10);
      fetchResponseOptionsList();
    }
  }, [currentUsecase, isLoading]);

  // Fetch statistics data
  const fetchStats = () => {
    if (!currentUsecase) return;

    setStatsLoading(true);
    fetchRawRecordsStats({
      clientId: currentUsecase.client_id,
      usecaseId: currentUsecase._id,
    })
      .then((response) => {
        setStatsData(response);
      })
      .catch((error) => {
        console.error("Failed to fetch statistics:", error);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  };

  // Fetch response options for dropdown
  const fetchResponseOptionsList = () => {
    if (!currentUsecase) return;

    setLoadingResponseOptions(true);
    fetchResponseOptions({
      clientId: currentUsecase.client_id,
      usecaseId: currentUsecase._id,
    })
      .then((options) => {
        const sortedOptions = options.sort((a, b) => a.localeCompare(b));
        setResponseOptions(sortedOptions);
      })
      .catch((error) => {
        console.error("Failed to fetch response options:", error);
      })
      .finally(() => {
        setLoadingResponseOptions(false);
      });
  };

  // Fetch records data
  const fetchRecordsData = (page = 1, pageSize = 10, appliedFilters = filters) => {
    if (!currentUsecase) return;

    setRecordsLoading(true);
    fetchRawRecords({
      clientId: currentUsecase.client_id,
      usecaseId: currentUsecase._id,
      page,
      pageSize,
      filters: appliedFilters
    })
      .then((response) => {
        setRecordsData(response.records || []);
        setPagination({
          ...pagination,
          current: page,
          pageSize: pageSize,
          total: response.total || 0
        });
      })
      .catch((error) => {
        console.error("Failed to fetch records:", error);
      })
      .finally(() => {
        setRecordsLoading(false);
      });
  };

  // Handle table changes (pagination)
  const handleTableChange = (pagination, tableFilters, sorter) => {
    const updatedFilters = {
      ...filters,
      exampleStatus: tableFilters.example_success && tableFilters.example_success.length > 0
        ? tableFilters.example_success[0]
        : undefined,
      responseStatus: tableFilters.response_success && tableFilters.response_success.length > 0
        ? tableFilters.response_success[0]
        : undefined,
    };

    setFilters(updatedFilters);
    fetchRecordsData(pagination.current, pagination.pageSize, updatedFilters);
  };

  // Handle viewing raw example
  const handleViewRawExample = (record) => {
    setSelectedRecord(record);
    setRawExampleVisible(true);
    setRawExampleLoading(false);
  };

  // Apply filter from modal
  const handleApplyFilterFromModal = (filterKey, value) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    setFormValues(prev => ({ ...prev, [filterKey]: value }));
    fetchRecordsData(1, pagination.pageSize, newFilters);
  };

  // Handle record update (after editing)
  const handleRecordUpdated = (updatedRecord) => {
    const updatedRecords = recordsData.map(record =>
      record.id === updatedRecord.id
        ? {
          ...record,
          raw_prompt: updatedRecord.raw_example,
          raw_response: updatedRecord.raw_response,
          request_example: updatedRecord.request_example,
          response: updatedRecord.response,
          example_success: updatedRecord.example_success,
          response_success: updatedRecord.response_success,
          example_failure_reason: updatedRecord.example_failure_reason,
          response_failure_reason: updatedRecord.response_failure_reason
        }
        : record
    );

    setRecordsData(updatedRecords);
    setSelectedRecord({
      ...selectedRecord,
      raw_prompt: updatedRecord.raw_example,
      raw_response: updatedRecord.raw_response,
      request_example: updatedRecord.request_example,
      response: updatedRecord.response,
      example_success: updatedRecord.example_success,
      response_success: updatedRecord.response_success,
      example_failure_reason: updatedRecord.example_failure_reason,
      response_failure_reason: updatedRecord.response_failure_reason
    });

    fetchStats();
  };

  // Handle refreshing data
  const handleRefreshData = () => {
    fetchStats();
    fetchRecordsData(pagination.current, pagination.pageSize);
    fetchResponseOptionsList();
  };

  // Handle filter drawer submission
  const handleFilterSubmit = () => {
    const newFilters = {
      ...filters,
      searchQuery: formValues.searchQuery ? formValues.searchQuery.trim() : '',
      rawPromptQuery: formValues.rawPromptQuery ? formValues.rawPromptQuery.trim() : '',
      rawResponseQuery: formValues.rawResponseQuery || '',
    };

    setFilters(newFilters);
    setFilterSubmitted(true);
    fetchRecordsData(1, pagination.pageSize, newFilters);
    setFilterDrawerVisible(false);

    setTimeout(() => {
      setFilterSubmitted(false);
    }, 3000);
  };

  // Clear a specific filter
  const handleClearFilter = (filterKey) => {
    let newValue = undefined;
    if (filterKey === 'exampleStatus' || filterKey === 'responseStatus') {
      newValue = undefined;
    } else {
      newValue = '';
    }

    const newFilters = { ...filters, [filterKey]: newValue };
    setFilters(newFilters);

    if (filterKey !== 'exampleStatus' && filterKey !== 'responseStatus') {
      setFormValues(prev => ({ ...prev, [filterKey]: newValue }));
    }

    fetchRecordsData(1, pagination.pageSize, newFilters);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    const newFilters = {
      exampleStatus: undefined,
      responseStatus: undefined,
      searchQuery: '',
      rawPromptQuery: '',
      rawResponseQuery: '',
    };

    setFilters(newFilters);
    setFormValues({ searchQuery: '', rawPromptQuery: '', rawResponseQuery: '' });

    if (filterDrawerVisible) {
      return;
    }

    fetchRecordsData(1, pagination.pageSize, newFilters);
  };

  // Create table columns with handlers
  const columns = createColumns({
    handleViewRawExample,
    filters
  });

  // Render active filters
  const renderActiveFilters = () => {
    if (activeFiltersCount === 0) return null;

    return (
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ marginRight: 4, color: 'var(--content-secondary)' }}>Active filters:</span>
        {filters.searchQuery && (
          <span className="spark-filter-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: '4px', background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', fontSize: 13 }}>
            ID: {filters.searchQuery}
            <button onClick={() => handleClearFilter('searchQuery')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
        {filters.exampleStatus && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: '4px', background: 'rgba(82, 196, 26, 0.15)', color: '#52c41a', fontSize: 13 }}>
            Example: {filters.exampleStatus === 'success' ? 'Successful' : (filters.exampleStatus === 'failed' ? 'Failed' : 'Not Processed')}
            <button onClick={() => handleClearFilter('exampleStatus')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
        {filters.responseStatus && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: '4px', background: 'rgba(160, 100, 255, 0.15)', color: '#a78bfa', fontSize: 13 }}>
            Response: {filters.responseStatus === 'success' ? 'Successful' : (filters.responseStatus === 'failed' ? 'Failed' : 'Not Processed')}
            <button onClick={() => handleClearFilter('responseStatus')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
        {filters.rawPromptQuery && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: '4px', background: 'rgba(255, 165, 0, 0.15)', color: '#ffa500', fontSize: 13 }}>
            Prompt contains: {filters.rawPromptQuery}
            <button onClick={() => handleClearFilter('rawPromptQuery')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
        {filters.rawResponseQuery && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: '4px', background: 'rgba(0, 200, 200, 0.15)', color: '#22d3ee', fontSize: 13 }}>
            Response contains: {filters.rawResponseQuery}
            <button onClick={() => handleClearFilter('rawResponseQuery')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
        <Button
          variant="ghost"
          size="small"
          startIcon={<SparkIcon slug="close" size="small" />}
          onClick={handleClearAllFilters}
        >
          Clear all
        </Button>
      </div>
    );
  };

  // Display loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Skeleton shapeType="circle" width="40px" height="40px" />
        <div style={{ marginTop: 16 }}>Loading raw records status...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 8 }}>Raw Records Processing Status</h4>
        <p style={{ color: 'var(--content-secondary)', fontSize: 14, lineHeight: 1.5 }}>
          This section provides detailed information about the processing status of the raw records in your dataset
          that were collected through the optune-sdk. It shows whether the examples and responses were successfully
          extracted from the prompts.
        </p>
      </div>

      <StatisticsSection loading={statsLoading} statsData={statsData} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
        <h5 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Raw Records</h5>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            onClick={() => setFilterDrawerVisible(true)}
            variant={activeFiltersCount > 0 ? "primary" : "secondary"}
            size="small"
            startIcon={<SparkIcon slug="filter" size="small" />}
            disabled={recordsLoading}
          >
            {activeFiltersCount > 0 ? `Filters (${activeFiltersCount})` : "Filters"}
          </Button>
          <Button
            onClick={handleRefreshData}
            variant="primary"
            size="small"
            startIcon={<SparkIcon slug="refresh" size="small" />}
            disabled={recordsLoading || statsLoading}
          >
            Refresh Data
          </Button>
        </div>
      </div>

      {filterSubmitted && !recordsLoading && (
        <div style={{ marginBottom: 16 }}>
          <AlertBanner
            text="Filters applied successfully"
            feedback="success"
            showButton={false}
            showCloseButton={true}
            onCloseClick={() => setFilterSubmitted(false)}
          />
        </div>
      )}

      {renderActiveFilters()}

      {activeFiltersCount > 0 && recordsData.length === 0 && !recordsLoading && (
        <div style={{ marginBottom: 16 }}>
          <AlertBanner
            text="No matching records found. Try adjusting your filters to see more results."
            feedback="warning"
            showButton={true}
            buttonLabel="Clear Filters"
            onActionClick={handleClearAllFilters}
            showCloseButton={false}
          />
        </div>
      )}

      <div style={{ marginBottom: 16, borderRadius: '8px' }}>
        <DataTable
          columns={columns}
          dataSource={recordsData}
          rowKey="id"
          loading={recordsLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page) => fetchRecordsData(page, pagination.pageSize),
          }}
          emptyText="No raw records found"
        />
      </div>

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--content-secondary)' }}>
        <SparkIcon slug="info" size="small" />
        <span style={{ fontSize: 13 }}>
          If you see failed records, you may need to adjust your Response Parser function or Prompt Template to properly handle all types of responses.
        </span>
      </div>

      {/* Advanced Filter Drawer */}
      <SparkDrawerPortal
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
      >
        <DrawerItem
          title="Advanced Filters"
          width={400}
          showCloseButton={true}
          onCloseClick={() => setFilterDrawerVisible(false)}
          showPrimaryButton={false}
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--content-secondary)' }}>
                {recordsLoading ? 'Loading...' : `${pagination.total} Records`}
              </span>
              <Badge amount={String(activeFiltersCount)} />
            </div>
          }
        >
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <Divider />
              <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--content-secondary)' }}>Basic Filters</p>

              <div style={{ marginBottom: 16 }}>
                <Input
                  label="Search by ID"
                  placeholder="Enter record ID to search"
                  showLabel={true}
                  showPrefixIcon={true}
                  value={formValues.searchQuery}
                  onChange={(e) => setFormValues(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
              </div>

              <Divider />
              <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--content-secondary)' }}>Content Search</p>

              <div style={{ marginBottom: 16 }}>
                <Input
                  label="Search in Prompts"
                  placeholder="Enter text to search in prompts"
                  showLabel={true}
                  showPrefixIcon={true}
                  showHelperText={true}
                  helperText="Case-insensitive search within prompt content"
                  value={formValues.rawPromptQuery}
                  onChange={(e) => setFormValues(prev => ({ ...prev, rawPromptQuery: e.target.value }))}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <DropdownMenu
                  selectType="single-select"
                  label="Search in Responses"
                  placeholder="Select or search in responses"
                  text={formValues.rawResponseQuery || undefined}
                  showLabel={true}
                  items={responseOptions.map(option => ({
                    label: option,
                    state: formValues.rawResponseQuery === option ? 'selected' : 'default',
                    onClick: () => setFormValues(prev => ({ ...prev, rawResponseQuery: option })),
                  }))}
                />
                <span style={{ display: 'block', fontSize: 12, color: 'var(--content-secondary)', marginTop: 4 }}>
                  Select from available response content or type to search
                </span>
              </div>
            </div>

            {/* Footer actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid var(--border-default, rgba(255,255,255,0.1))' }}>
              <Button
                variant="secondary"
                size="small"
                danger
                startIcon={<SparkIcon slug="close" size="small" />}
                onClick={handleClearAllFilters}
              >
                Clear All Filters
              </Button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => setFilterDrawerVisible(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  startIcon={<SparkIcon slug="filter" size="small" />}
                  onClick={handleFilterSubmit}
                  disabled={recordsLoading}
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </DrawerItem>
      </SparkDrawerPortal>

      <RawExampleModal
        visible={rawExampleVisible}
        onClose={() => setRawExampleVisible(false)}
        loading={rawExampleLoading}
        record={selectedRecord}
        onApplyFilter={handleApplyFilterFromModal}
        onRecordUpdated={handleRecordUpdated}
        clientId={currentUsecase?.client_id}
        usecaseId={currentUsecase?._id}
        setStatsData={setStatsData}
      />
    </div>
  );
};

export default RawRecordsStatusTable;
